/**
 * №22.3.1 (P2) — the ONE renderer for all chat-visible text.
 *
 * Two render paths existed: the console chat (`AgentThread`) rendered assistant text
 * through a full react-markdown component map (tables, lists, code), while the task
 * thread (`RunThreadSection`, a Sprint-22 restyle of the bubbles) rendered raw text
 * in a `<p white-space:pre-wrap>` — so `**bold**` showed literally in the docked panel
 * while tables rendered a line away. The 22.3 dock put both in the SAME panel, making
 * the split visible. This is the single renderer both now use; a fixture regression
 * (bold/italic/list/table/link) in the R5 suite guards the unification.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { type CSSProperties } from "react";

const inlineCode: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: "0.85em",
  background: "var(--color-background-tertiary)", padding: "1px 5px", borderRadius: 5,
};
const codeBlock: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.5,
  background: "var(--color-background-tertiary)", padding: "10px 12px", borderRadius: 8,
  overflowX: "auto", margin: "8px 0",
};

export function ChatMarkdown({ text }: { text: string }) {
  return (
    <div className="prose-kuvio" style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--color-text-primary)" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-2">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          h1: ({ children }) => <h1 className="my-3 text-base font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="my-2 text-sm font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="my-2 text-sm font-medium">{children}</h3>,
          h4: ({ children }) => <h4 className="my-2 text-sm font-medium">{children}</h4>,
          ul: ({ children }) => <ul className="my-2 list-disc pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-decimal pl-5">{children}</ol>,
          li: ({ children }) => <li className="my-0.5">{children}</li>,
          input: ({ checked, ...props }) => (
            <input type="checkbox" checked={checked} readOnly className="mr-1.5 translate-y-[1px]" {...props} />
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-lg" style={{ border: "var(--kuvio-border-default)" }}>
              <table className="w-full text-left text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead style={{ background: "var(--color-background-secondary)" }}>{children}</thead>,
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => <th className="px-2 py-1.5 font-medium">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1.5">{children}</td>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 pl-3 italic" style={{ borderLeft: "2px solid var(--color-border-secondary)", color: "var(--color-text-secondary)" }}>
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3" style={{ border: "none", borderTop: "var(--kuvio-border-default)" }} />,
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) return <code style={inlineCode}>{children}</code>;
            return (
              <pre style={codeBlock}>
                <code className={className}>{children}</code>
              </pre>
            );
          },
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-text-info)", textDecoration: "underline" }}>
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img src={typeof src === "string" ? src : undefined} alt={alt ?? ""} className="my-2 max-w-full rounded-lg" />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
